
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

/**
 * Creates or updates a Slack integration record
 */
export async function upsertSlackIntegration(
  aiStaffId: string,
  webhookUrl: string,
  slackToken: string,
  triggerTag: string = "@marketing",
  isFileProcessingEnabled: boolean = true,
  isAutoResponsesEnabled: boolean = true
): Promise<void> {
  try {
    logger.info({
      message: "Upserting Slack integration",
      context: { aiStaffId, triggerTag }
    });
    
    // Check if integration exists
    const { data: existingIntegration, error: fetchError } = await supabase
      .from('slack_integrations')
      .select('*')
      .eq('ai_staff_id', aiStaffId)
      .maybeSingle();
    
    if (fetchError) {
      logger.error({
        message: "Error checking existing integration",
        error: fetchError,
        context: { aiStaffId }
      });
      throw new Error(`Database fetch error: ${fetchError.message}`);
    }
    
    if (existingIntegration) {
      // Update existing integration
      const { error: updateError } = await supabase
        .from('slack_integrations')
        .update({
          webhook_url: webhookUrl,
          slack_token: slackToken,
          trigger_tag: triggerTag,
          is_file_processing_enabled: isFileProcessingEnabled,
          is_auto_responses_enabled: isAutoResponsesEnabled,
          updated_at: new Date().toISOString()
        })
        .eq('ai_staff_id', aiStaffId);
      
      if (updateError) {
        logger.error({
          message: "Error updating Slack integration",
          error: updateError,
          context: { aiStaffId }
        });
        throw new Error(`Update error: ${updateError.message}`);
      }
      
      logger.info({
        message: "Successfully updated Slack integration",
        context: { aiStaffId }
      });
    } else {
      // Create new integration
      const { error: insertError } = await supabase
        .from('slack_integrations')
        .insert({
          ai_staff_id: aiStaffId,
          webhook_url: webhookUrl,
          slack_token: slackToken,
          trigger_tag: triggerTag,
          is_file_processing_enabled: isFileProcessingEnabled,
          is_auto_responses_enabled: isAutoResponsesEnabled
        });
      
      if (insertError) {
        logger.error({
          message: "Error inserting Slack integration",
          error: insertError,
          context: { aiStaffId }
        });
        throw new Error(`Insert error: ${insertError.message}`);
      }
      
      logger.info({
        message: "Successfully created new Slack integration",
        context: { aiStaffId }
      });
    }
  } catch (error) {
    logger.error({
      message: "Error upserting Slack integration",
      error: error instanceof Error ? error : new Error(String(error)),
      context: { aiStaffId }
    });
    
    throw error;
  }
}
